/**
 * @lume/shared — Taxonomia de ações do conductor × nível de identidade exigido
 * (Fatia 2 PR4 — step-up determinístico). Spec §5.1.
 *
 * O conductor emite `intentClassification.action` (taxonomia abaixo) no output do
 * turno; o engine cruza `action → requiredLevel × assuranceLevel × vertical × papel`
 * e decide deterministicamente: já liberado / dispara OTP / handoff quente. Este é
 * o 1º consumidor em runtime de `riskActionsRequireVerified` (vive na
 * `IdentityProofingPolicy`, por vertical — não duplicamos aqui).
 *
 * **Configurável por vertical desde o desenho** (override esparso +
 * `intentRequirement`), mas semeado só p/ os verticais reais (saúde/jurídico/
 * varejo/cobrança/telecom) — sem UI agora, mesma premissa do
 * `IDENTITY_PROOFING_POLICIES`/Item 384.
 *
 * **`requiresWriteTool`** é o ponto de extensão da Fatia 10: ações que MUTAM
 * (cadastral_change/service_action) só liberam quando há uma tool de ESCRITA
 * disponível E o conector está em `read-write`. A write `service.schedule_visit`
 * já existe (contrato + executor + tool MCP `schedule-visit`); sob a trava
 * read-only (Etapa 1) ela é suprimida → handoff quente mesmo com `verified`. A
 * Etapa 2 a habilita por conector `read-write` opt-in (sem redesenho do step-up).
 */
export const INTENT_ACTIONS = [
    'public_info',
    'account_context',
    'sensitive_data_read',
    'billing_payment_code_read',
    'service_status_read',
    'service_connection_read',
    'service_ticket_read',
    'cadastral_change',
    // Consulta de DATAS/janelas disponíveis para agendar uma visita técnica (read MCP
    // `list-visit-slots`). Etapa 2 slots-first: o conductor oferece datas REAIS antes de
    // chamar `service_action` (schedule-visit) — evita agendar "no escuro" / rejeição.
    'service_slots_query',
    'service_action',
    'appointment_confirm',
    // Frente B Bloco II — agenda NATIVA (a Lume É a agenda do tenant sem ERP).
    // Distintas de `service_action` (despacho de campo roteado a equipe/ERP): aqui a
    // IA lê a disponibilidade real e CRAVA o slot no calendário nativo (guardado por
    // código). Native (sem tool MCP) — como `appointment_confirm`.
    'agenda_availability_query',
    'agenda_book',
    'agenda_reschedule',
    'agenda_cancel',
    // Frente B Bloco IV — ENTRAR na fila de espera (waitlist) quando o horário lotou
    // (capacidade de grupo cheia). Escrita NATIVA (cria `WaitlistEntry`, NÃO grava na
    // agenda) de BAIXA FRICÇÃO (`none`): declarar a preferência/janela de fila é como
    // consultar disponibilidade — o cliente entra na fila ANTES de se identificar; só ao
    // CRAVAR (promoção/aceite via `agenda_book`) o `verified` é exigido (a fila NÃO burla
    // a identidade). Roteada pelo conjunto `AGENDA_NATIVE_ACTIONS` (NativeScheduling/Waitlist).
    'agenda_join_waitlist',
    'out_of_scope',
];
const R = (requiredLevel, opts = {}) => ({
    requiredLevel,
    requiresTitular: opts.requiresTitular ?? false,
    requiresWriteTool: opts.requiresWriteTool ?? false,
    outOfScope: opts.outOfScope ?? false,
});
/**
 * Tabela default (§5.1). Universal entre verticais; o que VARIA por vertical é se a
 * falta de `verified` escala vs. tenta OTP — governado por `riskActionsRequireVerified`
 * (na policy do vertical), não aqui.
 */
export const INTENT_ACTION_REQUIREMENTS = {
    public_info: R('none'),
    account_context: R('self_declared'),
    // Leitura de dado sensível: `verified` libera a REVELAÇÃO (gate PR1) — sem tool de
    // escrita; é a ação chat-resolvível por OTP em v1 (caso âncora "valor da fatura").
    sensitive_data_read: R('verified'),
    // Código de PAGAMENTO de uma fatura/cobrança (PIX copia-e-cola, linha digitável/código
    // de barras do boleto). Dado financeiro sensível — MESMO gate de revelação que
    // `sensitive_data_read`/`read_invoice`: `verified`, SEM titular (chat-resolvível por OTP),
    // SEM tool de escrita (é leitura: a consulta `consultar-cobranca-boleto` não muta nada).
    billing_payment_code_read: R('verified'),
    // Consulta do STATUS de uma operação de serviço que o cliente possui (visita técnica,
    // pedido, consulta). Leitura de dado EXTERNO chaveado no personRef → `verified` (mesmo
    // gate de revelação + trilho de OTP de `sensitive_data_read`), SEM tool de escrita.
    service_status_read: R('verified'),
    // Diagnóstico de conexão AO VIVO ("internet caiu / tem sinal?"). Leitura de dado EXTERNO
    // chaveado no personRef → `verified` (mesmo gate de revelação + trilho de OTP de
    // `service_status_read`), SEM tool de escrita. Conceito telecom (sem variação por vertical).
    service_connection_read: R('verified'),
    // Status de um CHAMADO/ordem de serviço (OS)/protocolo já aberto ("foi resolvido?").
    // Leitura de dado EXTERNO chaveado no personRef → `verified` (mesmo gate/trilho de OTP de
    // `service_status_read`), SEM tool de escrita. Conceito universal (chamado/OS existe em
    // todo vertical) — sem variação por vertical.
    service_ticket_read: R('verified'),
    // Consulta de datas/janelas de visita disponíveis (read `list-visit-slots`). Chaveada
    // no personRef do contrato → `verified` (mesmo gate/trilho dos demais reads externos),
    // SEM tool de escrita. Slots-first: antecede `service_action`.
    service_slots_query: R('verified'),
    // Mutação cadastral: `verified` + titular + tool de escrita (Fatia 10).
    cadastral_change: R('verified', { requiresTitular: true, requiresWriteTool: true }),
    // Ação de serviço (2ª via/desbloqueio): conservador (`verified`) + tool de escrita.
    service_action: R('verified', { requiresWriteTool: true }),
    // AC3 — confirmar uma visita JÁ DECIDIDA pelo operador é uma ação na agenda do titular →
    // `verified`, SEM titular (a conversa de confirmação é dirigida à PESSOA daquela visita;
    // um contato autorizado verificado pode confirmar — não exige titularidade), SEM tool de
    // escrita (a confirmação é uma escrita NATIVA do Lume — `Appointment.status='confirmed'` —
    // NÃO uma tool MCP; logo não passa pelo executeToolCall nem exige `requiresWriteTool`).
    appointment_confirm: R('verified'),
    // Agenda nativa (Frente B Bloco II). CONSULTAR disponibilidade não é dado sensível
    // (são horários livres, iguais p/ todos) → `none`: um prospect vê os horários ANTES
    // de se identificar (UX de baixa fricção p/ barbearia/salão). Já GRAVAR (book/
    // reschedule/cancel) é ação consequente na agenda → `verified` (decisão herdada
    // [3.441]: confirmar exige verified). SEM tool de escrita MCP (a escrita é NATIVA,
    // guardada pelo NativeSchedulingService) — como `appointment_confirm`. Verticais de
    // baixa fricção podem relaxar `agenda_book` via `_BY_VERTICAL` (sem código).
    agenda_availability_query: R('none'),
    agenda_book: R('verified'),
    agenda_reschedule: R('verified'),
    agenda_cancel: R('verified'),
    // Frente B Bloco IV — entrar na FILA é baixa fricção (`none`), como consultar: não
    // grava na agenda (só `WaitlistEntry`). Cravar na promoção/aceite usa `agenda_book`
    // (verified). SEM tool de escrita MCP (write NATIVO) nem titular.
    agenda_join_waitlist: R('none'),
    out_of_scope: R(null, { outOfScope: true }),
};
export const INTENT_ACTION_REQUIREMENTS_BY_VERTICAL = {
    // Varejo/comercial é de baixa fricção → relaxa SÓ o nível de `service_action` (foco em
    // conversão; rebaixamento deliberado da DECISÃO 2, e o `consequenceFloor` soft o sustenta).
    // `requiresWriteTool`/`requiresTitular` NÃO são ditos ⇒ herdam o seed. Os demais verticais
    // herdam tudo; os de risco (saúde/jurídico/cobrança) escalam via `riskActionsRequireVerified`.
    commercial: {
        service_action: { requiredLevel: 'self_declared' },
    },
};
/**
 * Funde o override PARCIAL sobre o seed, faceta a faceta.
 *
 * ★ `undefined` explícito HERDA (não zera) — mesma armadilha que o Fable pegou na C1
 * (`{cadastral_change: undefined}` satisfaz `action in override`). Por isso NUNCA um spread
 * (`{...seed, ...override}`), que propagaria o `undefined` explícito por cima do seed.
 *
 * ★ As duas metades usam operadores DIFERENTES de propósito, e a diferença é de segurança:
 *   - **booleanos → `??`**: herdam tanto em `undefined` quanto em `null`. Um writer JSON futuro
 *     que mande `requiresWriteTool: null` cai na HERANÇA (o seed, conservador) em vez de ser
 *     coagido a falsy — com `!== undefined` o `null` seria atribuído e RELAXARIA. Direção certa.
 *   - **`requiredLevel` → `!== undefined`**: aqui `null` é valor LEGÍTIMO (`out_of_scope`), e um
 *     `??` o trocaria pelo seed em silêncio. Ausência é herança; `null` é declaração.
 */
function mergeRequirement(seed, override) {
    return {
        requiredLevel: override.requiredLevel !== undefined ? override.requiredLevel : seed.requiredLevel,
        requiresTitular: override.requiresTitular ?? seed.requiresTitular,
        requiresWriteTool: override.requiresWriteTool ?? seed.requiresWriteTool,
        outOfScope: override.outOfScope ?? seed.outOfScope,
    };
}
/** Resolve o requisito de uma ação no vertical (override PARCIAL sobre o seed → default). */
export function intentRequirement(action, vertical) {
    const seed = INTENT_ACTION_REQUIREMENTS[action];
    if (vertical != null && vertical in INTENT_ACTION_REQUIREMENTS_BY_VERTICAL) {
        const override = INTENT_ACTION_REQUIREMENTS_BY_VERTICAL[vertical][action];
        if (override !== undefined)
            return mergeRequirement(seed, override);
    }
    return seed;
}
/**
 * Escopo (nome de permissão) que cada ação EXIGE do papel do contato (Fatia 3 PR5,
 * §4.2). `null` = a ação não passa pelo gate de papel (info pública/genérica/fora de
 * escopo). O nome casa com `Playbook.rolePermissions[role].allowedScopes` e/ou
 * `BondAuthorizedContact.scopes` — composto por `roleAllowsAction` (role-scope.ts).
 *
 * Default = telecom/support (vertical real semeado NetComet): leitura/serviço
 * sensível exige `financeiro` (caso âncora "valor/2ª via da fatura"); mutação
 * cadastral exige `cadastral`.
 */
const INTENT_ACTION_SCOPE_DEFAULT = {
    public_info: null,
    account_context: null,
    sensitive_data_read: 'financeiro',
    // Código de pagamento = dado financeiro sensível → `financeiro` (mesmo escopo do
    // read_invoice). UNIVERSAL: um código de pagamento é instrumento financeiro em qualquer
    // vertical (sem override em `_BY_VERTICAL`) — diferente de `sensitive_data_read`, cujo
    // sentido muda por vertical (clínico/negociação).
    billing_payment_code_read: 'financeiro',
    service_status_read: 'suporte-tecnico',
    service_connection_read: 'suporte-tecnico', // diagnóstico de conexão = suporte (não financeiro)
    service_ticket_read: 'suporte-tecnico', // status de chamado/OS = suporte (não financeiro)
    service_slots_query: 'suporte-tecnico', // datas de visita = operação de serviço (igual service_action)
    cadastral_change: 'cadastral',
    service_action: 'suporte-tecnico', // era 'financeiro' — operação de serviço, não fatura (Fatia 10.x)
    // AC3 + Frente B Bloco III PR2 — confirmar visita exige o escopo de agenda
    // `appointment-confirmation`: além do nível `verified`, o titular controla QUEM
    // confirma (titular dá short-circuit em `roleAllowsAction`; um contato autorizado
    // só confirma se o titular lhe concedeu este escopo). Habilita o familiar autorizado
    // a confirmar pelo titular sem abrir a confirmação a qualquer verificado.
    appointment_confirm: 'appointment-confirmation',
    // Agenda nativa: o gate de identidade (none p/ consulta, verified p/ gravar) já é a
    // fronteira; sem escopo de papel adicional (a agenda é do tenant, não dado de 3º).
    agenda_availability_query: null,
    agenda_book: null,
    agenda_reschedule: null,
    agenda_cancel: null,
    // Fila de espera: a agenda é do tenant (não dado de 3º); o gate de nível já é a
    // fronteira → sem escopo de papel adicional (igual às demais ações de agenda nativa).
    agenda_join_waitlist: null,
    out_of_scope: null,
};
/**
 * Override esparso do NOME do escopo por vertical (design-for-platform). Mantido
 * SEPARADO de `IntentActionRequirement` (que carrega nível/titular/write-tool,
 * universais) — só o nome do escopo varia entre verticais, e separar evita
 * restatar o requirement inteiro. Estruturalmente override-por-tenant (sem UI).
 */
const INTENT_ACTION_SCOPE_BY_VERTICAL = {
    // Saúde: leitura clínica e autorização de retirada têm escopos próprios.
    healthcare: {
        sensitive_data_read: 'medical-decisions',
        service_action: 'pickup-authorization',
        service_status_read: 'pickup-authorization',
    },
    // Jurídico: leitura de estratégia/negociação × peticionamento.
    legal: {
        sensitive_data_read: 'negotiation',
        service_action: 'court-filing',
        service_status_read: 'court-filing',
    },
    // Varejo/comercial: baixa fricção — sem escopo de papel (governado só pelo nível).
    commercial: { sensitive_data_read: null, service_action: null, service_status_read: null },
    // Cobrança herda o default financeiro (RISK_VERTICAL escala antes via policy).
};
/**
 * Os verticais que TÊM override de escopo — accessor ESTREITO (C1), não o mapa.
 *
 * ★ Nasceu exportando o mapa; o Fable provou por execução que um `export` mutável de um seed que
 * alimenta o gate LIVE é um **canal de afrouxamento em runtime que contorna o piso**
 * (`INTENT_ACTION_SCOPE_BY_VERTICAL.support = {sensitive_data_read: null}` ⇒ `intentScope`
 * devolve `null` ⇒ o gate de papel some, sem passar por `applyScopeFloor` nem pelo portão).
 * Numa fatia cuja tese é «mexer na chave só pode afrouxar», criar essa superfície seria a tese
 * virada contra si. O accessor devolve chaves NOVAS a cada chamada ⇒ zero superfície de mutação,
 * e o mapa volta a ser privado (estado pré-C1 exato).
 */
export function scopeOverrideVerticals() {
    return Object.keys(INTENT_ACTION_SCOPE_BY_VERTICAL);
}
/**
 * Resolve o escopo requerido pela ação no vertical (override esparso → default). Este é o **SEED
 * DE PLATAFORMA** do eixo de escopo; a camada config-aware (com PISO) vive em
 * `scope-vocabulary.ts` — molde exato de `risk-category.ts` (seed) × `action-risk-config.ts`
 * (config), que existe para não fazer `intent-actions` importar `risk-category` (ciclo).
 */
export function intentScope(action, vertical) {
    if (vertical != null && vertical in INTENT_ACTION_SCOPE_BY_VERTICAL) {
        const override = INTENT_ACTION_SCOPE_BY_VERTICAL[vertical];
        if (action in override)
            return override[action] ?? null;
    }
    return INTENT_ACTION_SCOPE_DEFAULT[action];
}
const INTENT_ACTION_RELATION_ENTITY = {
    public_info: 'none',
    account_context: 'account',
    sensitive_data_read: 'contract',
    billing_payment_code_read: 'contract',
    service_status_read: 'contract',
    service_connection_read: 'contract',
    service_ticket_read: 'contract',
    service_slots_query: 'contract',
    cadastral_change: 'account',
    service_action: 'contract',
    appointment_confirm: 'contract',
    agenda_availability_query: 'none',
    agenda_book: 'none',
    agenda_reschedule: 'none',
    agenda_cancel: 'none',
    agenda_join_waitlist: 'none',
    out_of_scope: 'none',
};
/** Resolve a entidade da relação da ação (seed default de plataforma; A6 Part 2). */
export function intentRelationEntity(action) {
    return INTENT_ACTION_RELATION_ENTITY[action];
}
const INTENT_ACTION_SET = new Set(INTENT_ACTIONS);
/** Type guard — a `action` emitida pelo conductor está na taxonomia? (fail-open a jusante.) */
export function isIntentAction(value) {
    return INTENT_ACTION_SET.has(value);
}
/**
 * Ações de agenda NATIVA (Frente B Bloco II) — atendidas pelo `NativeSchedulingService`
 * (não MCP, não escrita-confirmação-AC3). O conductor ramifica por este conjunto ANTES
 * da resolução de tool MCP. `agenda_availability_query` é leitura; as demais gravam.
 */
export const AGENDA_NATIVE_ACTIONS = [
    'agenda_availability_query',
    'agenda_book',
    'agenda_reschedule',
    'agenda_cancel',
    // Frente B Bloco IV — entrar na fila de espera é atendido nativamente (cria
    // `WaitlistEntry` via WaitlistService), mesma fronteira que as demais (não MCP).
    'agenda_join_waitlist',
];
const AGENDA_NATIVE_SET = new Set(AGENDA_NATIVE_ACTIONS);
/** A ação é de agenda nativa (roteada ao NativeSchedulingService)? */
export function isAgendaNativeAction(value) {
    return AGENDA_NATIVE_SET.has(value);
}
//# sourceMappingURL=intent-actions.js.map